import { ApolloClient, ApolloLink, InMemoryCache } from "@apollo/client";

declare module "@apollo/client" {
    namespace ApolloClient {
        namespace DeclareDefaultOptions {
            interface WatchQuery { errorPolicy: "all" }
            interface Query { errorPolicy: "all" }
        }
    }
    interface TypeOverrides {
        signatureStyle: "classic";
    }
}

import { ApolloProvider } from "@apollo/client/react";
import { setContext } from "@apollo/client/link/context";
import { HttpLink } from "@apollo/client/link/http";
import { RetryLink } from "@apollo/client/link/retry";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { getMainDefinition } from "@apollo/client/utilities";
import { Kind, OperationTypeNode } from "graphql";
import { createClient as createWsClient } from "graphql-ws";
import { PropsWithChildren, useMemo, useState, useRef, useEffect } from "react";
import {
    createAuthErrorLink,
    type AuthErrorLinkOptions,
} from "../lib/apollo/auth-error-link";
import { createWsConnectFailureCounter } from "../lib/apollo/ws-connect-failures";

export interface OidcApolloProviderConfig {
    /** GraphQL HTTP endpoint URL */
    httpLink: string;
    /** GraphQL WebSocket endpoint URL */
    wsLink: string;
    /** Log prefix for debug messages */
    logPrefix?: string;
}

export interface OidcApolloProviderProps extends PropsWithChildren {
    /** Access token for authentication */
    token?: string;
    /** Optional tenant id or slug for impersonation (Venus feature) */
    impersonateTenantId?: string;
    /** Configuration for endpoints */
    config: OidcApolloProviderConfig;
    /** Auth error handling callbacks */
    authErrorOptions: Omit<
        AuthErrorLinkOptions,
        "onSchemaMismatch" | "logPrefix"
    >;
    /** Optional custom HTTP link (e.g., ChromeFetchLink for Neptune) */
    customHttpLink?: ApolloLink;
    /** Component to render on schema mismatch error */
    SchemaMismatchErrorComponent?: React.ComponentType<{
        errorMessage: string;
    }>;
}

/**
 * Shared Apollo Provider with OIDC authentication support.
 * Used by both Venus and Neptune for consistent GraphQL client setup.
 *
 * Key features:
 * - WebSocket subscriptions with token authentication
 * - Automatic token refresh on 401 errors
 * - Optional impersonation support (Venus)
 * - Optional custom HTTP link (Neptune Chrome extension)
 */
export default function OidcApolloProvider({
    children,
    token,
    impersonateTenantId,
    config,
    authErrorOptions,
    customHttpLink,
    SchemaMismatchErrorComponent,
}: OidcApolloProviderProps) {
    const [schemaMismatchError, setSchemaMismatchError] = useState<
        string | null
    >(null);

    // Use refs for callbacks to avoid recreating Apollo client when callbacks change
    const authErrorOptionsRef = useRef(authErrorOptions);
    useEffect(() => {
        authErrorOptionsRef.current = authErrorOptions;
    }, [authErrorOptions]);

    // Read token and impersonateTenantId from refs at request time so the client
    // doesn't need to be recreated when they change. Recreating the client on
    // every tenant switch (e.g. for lytra_staff users navigating between emails)
    // causes a cascade of effect re-runs, cancels in-flight queries, and can
    // leave appState.loading stuck true, triggering the safety timer's reload.
    const tokenRef = useRef(token);
    tokenRef.current = token;
    const impersonateTenantIdRef = useRef(impersonateTenantId);
    impersonateTenantIdRef.current = impersonateTenantId;

    // Single-flight guard: a WS 4403 can repeat on every reconnect attempt,
    // so we only want one refresh in progress at a time.
    const wsRefreshInFlightRef = useRef(false);

    // Create Apollo Client - only recreate on endpoint/link changes, not token/tenant
    const client = useMemo(() => {
        const logPrefix = config.logPrefix || "Apollo";
        console.log(
            `${logPrefix} Client - Creating client`,
            token ? "(with token)" : "(no token)",
        );

        // Triggered when the server rejects a WS connection with close code 4403.
        // Without this, graphql-ws would retry forever using the same stale token.
        const refreshAuthForWs = async () => {
            if (wsRefreshInFlightRef.current) return;
            wsRefreshInFlightRef.current = true;
            const { onTokenRefresh, onTokenRefreshSuccess, onTokenRefreshFailed } =
                authErrorOptionsRef.current;
            try {
                const result = await onTokenRefresh();
                if (result?.access_token) {
                    onTokenRefreshSuccess?.(result.access_token);
                }
            } catch (error) {
                onTokenRefreshFailed(error);
            } finally {
                wsRefreshInFlightRef.current = false;
            }
        };

        const wsConnectFailures = createWsConnectFailureCounter();

        // WebSocket link for subscriptions
        const wsLink = new GraphQLWsLink(
            createWsClient({
                url: config.wsLink,
                connectionParams: () => {
                    // Send auth via ConnectionInit payload (not URL) to avoid
                    // leaking tokens into server logs, Sentry, and Referer headers.
                    // Read from refs so WS reconnects pick up the latest values
                    // without recreating the entire client.
                    const params: Record<string, string> = {};
                    if (tokenRef.current) params.accessToken = tokenRef.current;
                    if (impersonateTenantIdRef.current)
                        params.impersonateTenant = impersonateTenantIdRef.current;
                    return params;
                },
                retryAttempts: Infinity,
                shouldRetry: () => true,
                keepAlive: 0, // Disabled: no application-level handler acts on missing pongs. Uvicorn WS-level pings (20s) keep the TCP connection alive.
                retryWait: async (retries) => {
                    const maxDelay = 30000; // 30 seconds
                    const delay = Math.min(
                        1000 * Math.pow(2, retries),
                        maxDelay,
                    );
                    await new Promise((resolve) => setTimeout(resolve, delay));
                },
                on: {
                    connected: () => {
                        wsConnectFailures.connected();
                        console.log(`${logPrefix} WS connected`);
                    },
                    closed: (event) => {
                        const closeEvent = event as CloseEvent;
                        if (closeEvent.code === undefined) {
                            console.warn(`${logPrefix} WS closed`, event);
                            return;
                        }
                        // 1000 = normal closure, 1001 = going away (tab/nav),
                        // 1006 = abnormal TCP drop (network blip, Outlook taskpane
                        // sleep/wake), 1012 = server restart, 1013 = try again later,
                        // 4403 = auth rejection (handled below). All are expected
                        // lifecycle events with too many benign causes to be
                        // actionable; keep them out of Sentry.
                        const isExpected =
                            closeEvent.code === 1000 ||
                            closeEvent.code === 1001 ||
                            closeEvent.code === 1006 ||
                            closeEvent.code === 1012 ||
                            closeEvent.code === 1013 ||
                            closeEvent.code === 4403;
                        (isExpected ? console.debug : console.warn)(
                            `${logPrefix} WS closed`,
                            `code=${closeEvent.code}`,
                            `reason=${closeEvent.reason || "(none)"}`,
                            `wasClean=${closeEvent.wasClean}`,
                        );
                        if (closeEvent.code === 4403) {
                            void refreshAuthForWs();
                        }
                    },
                    error: (error) => {
                        // The graphql-ws error handler receives a browser Event on
                        // connection failure, not an Error object. Extracting useful
                        // fields avoids the useless "[object Event]" log.
                        if (error instanceof Error) {
                            console.error(
                                `${logPrefix} WS error (protocol/session)`,
                                error.message,
                                error,
                            );
                        } else {
                            const event = error as Event;
                            const target = event.target as WebSocket | null;
                            // Strip query params to avoid leaking access tokens into Sentry
                            const rawUrl = target?.url ?? config.wsLink;
                            const safeUrl = rawUrl.split("?")[0];
                            // readyState 3 = CLOSED: socket never connected or was rejected
                            // readyState 0 = CONNECTING: still attempting handshake
                            const state = target?.readyState;
                            const isTlsMismatch =
                                safeUrl.startsWith("wss://localhost") ||
                                safeUrl.startsWith("wss://127.0.0.1");
                            const hint =
                                state === WebSocket.CLOSED && isTlsMismatch
                                    ? " (TLS handshake failed -- local server likely does not support wss://, use VITE_HTTP_LINK=http://... to derive ws://)"
                                    : state === WebSocket.CLOSED
                                      ? " (connection failed -- server may be unreachable)"
                                      : "";
                            // One failed reconnect after sleep or a network change is
                            // routine and graphql-ws retries forever; only a sustained
                            // outage (or the local TLS mismatch) is worth an error,
                            // which Sentry's console capture reports.
                            const isSustained = wsConnectFailures.failed();
                            (isSustained || isTlsMismatch ? console.error : console.debug)(
                                `${logPrefix} WS error (connection)`,
                                `wsUrl=${safeUrl}`,
                                `readyState=${state ?? "unknown"}${hint}`,
                            );
                        }
                    },
                },
            }),
        );

        // HTTP link - use custom link if provided, otherwise create standard HttpLink
        const httpLink =
            customHttpLink || new HttpLink({ uri: config.httpLink });

        // Auth link - adds authorization header to HTTP requests.
        // Read from refs so changes to token/impersonation apply immediately
        // without recreating the client.
        const authLink = setContext((_, { headers }) => {
            const newHeaders: Record<string, string> = {
                ...(headers as Record<string, string>),
            };

            // Only include authorization header if token is present
            if (tokenRef.current) {
                newHeaders.authorization = `Bearer ${tokenRef.current}`;
            }

            // Add impersonation header if present (Venus feature)
            if (impersonateTenantIdRef.current) {
                newHeaders["X-Impersonate-Tenant"] = impersonateTenantIdRef.current;
            }

            return { headers: newHeaders };
        });

        // Error link for token refresh and schema mismatch handling
        // Use ref to get latest callbacks without recreating client
        const errorLink = createAuthErrorLink({
            onTokenRefresh: () =>
                authErrorOptionsRef.current.onTokenRefresh?.(),
            onTokenRefreshFailed: (error) =>
                authErrorOptionsRef.current.onTokenRefreshFailed?.(error),
            onTokenRefreshSuccess: (newToken) =>
                authErrorOptionsRef.current.onTokenRefreshSuccess?.(newToken),
            onSchemaMismatch: setSchemaMismatchError,
            logPrefix,
        });

        // RetryLink automatically retries failed GraphQL requests (e.g., network errors, 5xx responses).
        // This improves reliability by handling transient failures without user intervention.
        // - initial: 300ms delay before first retry
        // - max: caps delay at 30 seconds for subsequent retries
        // - jitter: adds randomness to prevent thundering herd when many clients retry simultaneously
        // - max attempts: 3 retries before giving up
        // - Does NOT retry 401/403 errors (those need token refresh, handled by errorLink)
        // - Does NOT retry 410: the offboarding gate's answer is final
        const retryLink = new RetryLink({
            delay: {
                initial: 300,
                max: 30000,
                jitter: true,
            },
            attempts: {
                max: 3,
                retryIf: (error) => {
                    const statusCode =
                        (error as any)?.statusCode ||
                        (error as any)?.networkError?.statusCode;
                    if (statusCode === 401 || statusCode === 403 || statusCode === 410) {
                        return false;
                    }
                    return !!error;
                },
            },
        });

        // Chain links: error -> retry -> auth -> http
        const httpWithAuth = ApolloLink.from([
            errorLink,
            retryLink,
            authLink,
            httpLink,
        ]);

        // Split link: route subscriptions and WS-flagged operations to WebSocket
        const splitLink = ApolloLink.split(
            ({ query, getContext }) => {
                const definition = getMainDefinition(query);
                // Always route subscriptions over WS
                if (
                    definition.kind === Kind.OPERATION_DEFINITION &&
                    definition.operation === OperationTypeNode.SUBSCRIPTION
                ) {
                    return true;
                }
                // Allow mutations/queries to opt-in to WS transport
                // via context: { useWebSocket: true }
                const context = getContext?.() as { useWebSocket?: boolean } | undefined;
                return context?.useWebSocket === true;
            },
            wsLink,
            httpWithAuth,
        );

        return new ApolloClient({
            link: splitLink,
            cache: new InMemoryCache({
                typePolicies: {
                    // Its id is the dataset's customer request id, shared by
                    // every run on the dataset version, while its labels belong
                    // to one run's case: normalised by id, a compared run's
                    // labels would overwrite this run's.
                    EvalReviewCustomerRequest: { keyFields: false },
                    Message: {
                        fields: {
                            // The server's messageFacts list is
                            // authoritative — when it shrinks (a fact was
                            // removed) the incoming array IS the new truth.
                            // Without an explicit merge fn, Apollo warns
                            // "Cache data may be lost when replacing the
                            // messageFacts field" on every
                            // post-deletion refetch. Accept incoming as is.
                            messageFacts: {
                                merge(_existing, incoming) {
                                    return incoming;
                                },
                            },
                        },
                    },
                },
            }),
            dataMasking: false,
            defaultOptions: {
                watchQuery: {
                    errorPolicy: "all",
                    fetchPolicy: "cache-first",
                },
                query: {
                    errorPolicy: "all",
                    fetchPolicy: "cache-first",
                },
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        config.httpLink,
        config.wsLink,
        config.logPrefix,
        customHttpLink,
    ]);

    // When the impersonated tenant changes, clear the Apollo cache so components
    // refetch data for the new tenant. Skip on initial mount (isMountedRef guard).
    const isMountedRef = useRef(false);
    useEffect(() => {
        if (!isMountedRef.current) {
            isMountedRef.current = true;
            return;
        }
        // resetStore() refetches every active query, and two of its rejections
        // are expected when the tenant changes mid-fetch (lytra_staff
        // fast-switching tenants): Apollo InvariantError #89 ("Store reset while
        // query was in flight"), and an AbortError when a query is torn down
        // before its refetch returns. Each query still reports its own errors;
        // swallow these two so they don't surface as unhandled rejections and
        // rethrow anything else.
        void client.resetStore().catch((e: unknown) => {
            const { name, message } = (e ?? {}) as { name?: unknown; message?: unknown };
            const msg = String(message ?? "");
            const isStoreReset =
                msg.toLowerCase().includes("store reset while query was in flight") ||
                (msg.includes("go.apollo.dev") && msg.includes("89"));
            const isAbort = name === "AbortError";
            if (!isStoreReset && !isAbort) throw e;
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [impersonateTenantId]);

    // Show schema mismatch error if detected
    if (schemaMismatchError && SchemaMismatchErrorComponent) {
        return (
            <SchemaMismatchErrorComponent errorMessage={schemaMismatchError} />
        );
    }

    return <ApolloProvider client={client}>{children}</ApolloProvider>;
}
